/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { KeyValue } from "~/generated/graphql";

export enum PatchPolicy {
  systemAll = "SystemAll",
  securityOnly = "SecurityOnly",
  custom = "Custom",
  customerManaged = "CustomerManaged",
}

export const patchPolicyRules: Record<
  string,
  {
    defaultSettings: Record<string, string>; // inputs will be hidden and the values will be set to these defaults
    visibleParams?: string[]; // only these params will be shown in the UI, if undefined all params will be shown
  }
> = {
  SystemAll: {
    defaultSettings: {
      AmazonLinuxClassification: '["All"]',
      AmazonLinuxInstallNonSecurity: "true",
      AmazonLinuxSeverity: '["All"]',
      CentosClassification: '["All"]',
      CentosInstallNonSecurity: "true",
      CentosSeverity: '["All"]',
      RhelClassification: '["All"]',
      RhelInstallNonSecurity: "true",
      RhelSeverity: '["All"]',
      SuseClassification: '["All"]',
      SuseSeverity: '["All"]',
      UbuntuInstallNonSecurity: "true",
      UbuntuSeverity: '["All"]',
      WindowsClassification: '["All"]',
      WindowsSeverity: '["All"]',
    },
  },
  SecurityOnly: {
    defaultSettings: {
      AmazonLinuxClassification: '["Security","Bugfix"]',
      AmazonLinuxInstallNonSecurity: "false",
      AmazonLinuxSeverity: '["Critical","Important"]',
      CentosClassification: '["Security","Bugfix"]',
      CentosInstallNonSecurity: "false",
      CentosSeverity: '["Critical","Important"]',
      RhelClassification: '["Security","Bugfix"]',
      RhelInstallNonSecurity: "false",
      RhelSeverity: '["Critical","Important"]',
      SuseClassification: '["Security"]',
      SuseSeverity: '["Critical","Important"]',
      UbuntuInstallNonSecurity: "false",
      UbuntuSeverity: '["Required","Important","Standard","Optional","Extra"]',
      WindowsClassification: '["CriticalUpdates","SecurityUpdates"]',
      WindowsSeverity: '["Critical","Important"]',
    },
  },
  Custom: {
    defaultSettings: {},
  },
  CustomerManaged: {
    defaultSettings: {},
    visibleParams: [
      "PatchPolicy",
      "BaselineSource",
      "HttpProxy",
      "HttpsProxy",
      "NoProxy",
      "RebootAllowed",
    ],
  },
};

export const getPatchPolicy = (
  parameters?: KeyValue[]
): PatchPolicy | undefined => {
  return parameters?.find((param) => param.key === "PatchPolicy")
    ?.value as PatchPolicy;
};

export const getVisiblePatchPolicyParams = (
  parameters?: KeyValue[],
  patchPolicy?: PatchPolicy
) => {
  if (!patchPolicy || !parameters) {
    return [];
  }

  return (
    parameters?.filter((param) =>
      patchPolicyRules[patchPolicy].visibleParams?.includes(param.key)
    ) ?? []
  );
};
